import { useState, useEffect, useMemo, useCallback } from "react";
import { useCenterData, tipo_control } from "./hooks/useCenterData";
import { useCentersDataAllSafe } from "./hooks/useCentersDataAllSafe";
import { getCenterIdentity, saveToCenterCache } from "./cache/centerCache";
import { useServer } from "../../context/ServerContext";
import useAssistantViewContext from "../../features/ai-assistant/useAssistantViewContext";
import useAssistantNanooxContext from "../../features/ai-assistant/useAssistantNanooxContext";
import { buildAssistantViewContext } from "../../features/ai-assistant/documentationKeyResolver";
import { buildAssistantNanooxContext } from "./utils/buildAssistantNanooxContext";

import { TooltipModeProvider } from "./context/TooltipModeContext";
import { TooltipProvider } from "./context/TooltipContext";
import { ModuleControlsProvider } from "./context/ModuleControlsContext";

import HomeNanoox from "./pages/Home/HomeNanoox";
import Dashboard from "./pages/Dashboard/Dashboard";
import Cargando from "./pages/Home/ui/Cargando";
import data from "./utils/data.json"

import "./shared/styles/variables.css";
import "./shared/styles/globals.css";

/* ================================
   Utils
================================ */

function getCentersFromStorage() {
  try {
    const raw = localStorage.getItem("listCenter");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const uniqueCenters = new Map();
    parsed.forEach((center) => {
      const key = getCenterIdentity(center);
      if (key && !uniqueCenters.has(key)) uniqueCenters.set(key, center);
    });
    return Array.from(uniqueCenters.values());
  } catch {
    return [];
  }
}

function markCenterUsage(center) {
  const raw = localStorage.getItem("center_usage") || "{}";
  const usage = JSON.parse(raw);
  usage[getCenterIdentity(center)] = Date.now();
  localStorage.setItem("center_usage", JSON.stringify(usage));
}

function sortCentersByUsage(centros) {
  const usage = JSON.parse(localStorage.getItem("center_usage") || "{}");
  return [...centros].sort(
    (a, b) => (usage[getCenterIdentity(b)] || 0) - (usage[getCenterIdentity(a)] || 0)
  );
}

const VIEW_STORAGE_KEY = "nanoox_active_view";

function normalizeValue(value) {
  return String(value || "").trim().toLowerCase();
}

// Firma estable de la lista de centros: getCentersFromStorage() devuelve un
// array nuevo en cada llamada y cualquier cambio de identidad reinicia la
// carga del dashboard desde el primer centro.
function serializeCenters(centros) {
  return JSON.stringify(
    (Array.isArray(centros) ? centros : []).map((item) => [
      normalizeValue(item?.region),
      normalizeValue(item?.area),
      normalizeValue(item?.centro),
    ])
  );
}

function parseDateTime(value) {
  if (!value || typeof value !== "string") return 0;
  const parsed = Date.parse(value.replace(" ", "T"));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function tokenizeIdentifier(value) {
  return String(value || "")
    .split("-")
    .map((part) => normalizeValue(part))
    .filter(Boolean);
}

function findCenterMetadata(center, centrosDisponibles) {
  const fallbackCentroId = center?.modulos?.[0]?.cans?.[0]?.centro_id;
  const tokens = [
    ...tokenizeIdentifier(center?.centro),
    ...tokenizeIdentifier(fallbackCentroId),
    ...tokenizeIdentifier(center?.area),
    ...tokenizeIdentifier(center?.region),
  ];

  let bestMatch = null;
  let bestScore = -1;

  for (const item of centrosDisponibles) {
    const centro = normalizeValue(item?.centro);
    const area = normalizeValue(item?.area);
    const region = normalizeValue(item?.region);

    let score = 0;
    if (tokens.includes(centro)) score += 4;
    if (tokens.includes(area)) score += 2;
    if (tokens.includes(region)) score += 2;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = item;
    }
  }

  return bestScore > 0 ? bestMatch : null;
}

function getCenterKey(center, centrosDisponibles = []) {
  const metadata = findCenterMetadata(center, centrosDisponibles);
  if (metadata?.centro) return getCenterIdentity(metadata);

  const directKey = getCenterIdentity(center);
  if (directKey.includes("|")) return directKey;

  const fallbackCentroId = center?.modulos?.[0]?.cans?.[0]?.centro_id;
  const candidates = [
    ...tokenizeIdentifier(center?.centro),
    ...tokenizeIdentifier(fallbackCentroId),
  ];

  return candidates.length >= 3
    ? candidates.slice(-3).join("|")
    : candidates[candidates.length - 1] || "";
}

function mergeCenterMetadata(center, centrosDisponibles) {
  const metadata = findCenterMetadata(center, centrosDisponibles) || {};
  const estadoCentro =
    center?.estado_centro ??
    center?.estado ??
    metadata?.estado_centro ??
    metadata?.estado ??
    null;

  return {
    ...center,
    region: metadata?.region || center?.region,
    area: metadata?.area || center?.area,
    centro: metadata?.centro || center?.centro,
    estado_centro: estadoCentro,
  };
}

/* ================================
   Main
================================ */

export default function MainNanoox({
  translate,
  handleClick,
  openMenuIdioma,
  handleCloseIdioma,
  handleLanguageChange,
  idioma,}) 
  {
  const { servidor } = useServer();

  const [selectedCenter, setSelectedCenter] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [showDashboardState, setShowDashboardState] = useState(
    () => localStorage.getItem(VIEW_STORAGE_KEY) === "dashboard"
  );
  const [centrosDisponibles, setCentrosDisponibles] = useState(getCentersFromStorage);
  const [controlType, setControlType] = useState("");
  const version = '2026.06.24';

  const setShowDashboard = useCallback((value) => {
    setShowDashboardState((prev) => {
      const nextValue = typeof value === "function" ? value(prev) : value;
      localStorage.setItem(
        VIEW_STORAGE_KEY,
        nextValue ? "dashboard" : "home"
      );
      return nextValue;
    });
  }, []);

  /* ================================
     Centro activo
  ================================ */

  const {
    centerData,
    initialLoading,
    error,
    refetch,
  } = useCenterData({
    region: showDashboardState ? undefined : selectedCenter?.region,
    area: showDashboardState ? undefined : selectedCenter?.area,
    centro: showDashboardState ? undefined : selectedCenter?.centro,
    servidor,
  });

  /* ================================
     Multi-centros seguro
  ================================ */

  const {
    data: allCentersData,
    loading: loadingCenters,
    error: centersError,
    startLoading,
  } = useCentersDataAllSafe();

  const assistantViewContext = useMemo(
    () => buildAssistantViewContext({
      service: "nanoox",
      module: showDashboardState ? "dashboard" : "general",
      view: "overview",
      entities: {
        region: selectedCenter?.region || localStorage.getItem("REGION") || "",
        area: selectedCenter?.area || localStorage.getItem("AREA") || "",
        centro: selectedCenter?.centro || localStorage.getItem("CENTRO") || "",
      },
      filters: {
        controlType,
      },
      uiState: {
        showDashboard: showDashboardState,
        loadingCenters,
        initialLoading,
        controlType: controlType || null,
      },
    }),
    [controlType, initialLoading, loadingCenters, selectedCenter, showDashboardState]
  );

  useAssistantViewContext(assistantViewContext);

  const nanooxContextDescriptor = useMemo(
    () =>
      buildAssistantNanooxContext({
        centerData,
        selectedCenter,
        controlType,
        showDashboard: showDashboardState,
        allCentersData,
        generadores: centerData?.generadores,
        loading: initialLoading || loadingCenters,
      }),
    [centerData, selectedCenter, controlType, showDashboardState, allCentersData, initialLoading, loadingCenters]
  );

  useAssistantNanooxContext(nanooxContextDescriptor);

  /* ================================
     Init centros
  ================================ */

  useEffect(() => {
    const syncCentersFromStorage = () => {
      const storedCenters = getCentersFromStorage();
      setCentrosDisponibles((previous) =>
        serializeCenters(previous) === serializeCenters(storedCenters)
          ? previous
          : storedCenters
      );
    };

    const handleStorage = (event) => {
      if (event.key === "listCenter") {
        syncCentersFromStorage();
      }
    };

    syncCentersFromStorage();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", syncCentersFromStorage);
    window.addEventListener("nanoox-centers-updated", syncCentersFromStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", syncCentersFromStorage);
      window.removeEventListener("nanoox-centers-updated", syncCentersFromStorage);
    };
  }, []);

  /* ================================
     Hidratar centro inicial
  ================================ */

  useEffect(() => {
    if (selectedCenter) return;

    const region = localStorage.getItem("REGION");
    const area = localStorage.getItem("AREA");
    const centro = localStorage.getItem("CENTRO");

    if (region && area && centro) {
      const metadata = findCenterMetadata({ region, area, centro }, centrosDisponibles);
      setSelectedCenter({
        region,
        area,
        centro,
        estado_centro: metadata?.estado_centro ?? metadata?.estado ?? null,
      });
    }
  }, [centrosDisponibles, selectedCenter]);

  useEffect(() => {
    if (!selectedCenter?.centro || selectedCenter?.estado_centro || selectedCenter?.estado) return;

    const metadata = findCenterMetadata(selectedCenter, centrosDisponibles);
    const estadoCentro = metadata?.estado_centro ?? metadata?.estado ?? null;

    if (!estadoCentro) return;

    setSelectedCenter((current) =>
      current?.centro === selectedCenter.centro
        ? { ...current, estado_centro: estadoCentro }
        : current
    );
  }, [centrosDisponibles, selectedCenter]);

  /* ================================
     Tipo control
  ================================ */

  useEffect(() => {
    tipo_control(servidor).then(r =>
      setControlType(r?.datos || "")
    );
  }, [servidor]);

  /* ================================
     Refetch cada 60s (solo con centro)
  ================================ */

  useEffect(() => {
    if (!selectedCenter) return;
    if (showDashboardState) return;

    const interval = setInterval(refetch, 60000);
    return () => clearInterval(interval);
  }, [refetch, selectedCenter, showDashboardState]);

  /* ================================
     Telemetría
  ================================ */

  useEffect(() => {
    if (selectedCenter?.centro) {
      markCenterUsage(selectedCenter);
    }
  }, [selectedCenter]);

  /* ================================
     Prefetch inteligente
  ================================ */

  useEffect(() => {
    if (!userInteracted) return;
    if (!centerData || showDashboardState || !selectedCenter) return;

    const ordered = sortCentersByUsage(centrosDisponibles)
      .filter(c => getCenterIdentity(c) !== getCenterIdentity(selectedCenter));

    startLoading({
      centros: ordered.slice(0, 5),
      servidor,
      delay: 800,
    });
  }, [centerData, userInteracted, showDashboardState, selectedCenter, centrosDisponibles, servidor, startLoading]);

  /* ================================
     Dashboard
  ================================ */

  useEffect(() => {
    if (!showDashboardState) return;
    if (!centrosDisponibles.length || !servidor) return;

    const loadDashboardData = () => {
      startLoading({
        centros: centrosDisponibles,
        servidor,
        delay: 500,
      });
    };

    loadDashboardData();

    const interval = setInterval(loadDashboardData, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [showDashboardState, centrosDisponibles, servidor, startLoading]);

  const dashboardSourceData = useMemo(() => {
    const combinedCenters = [...allCentersData];
    const currentKey = centerData
      ? getCenterKey(centerData, centrosDisponibles)
      : "";
    // Solo se mezcla el centro abierto si sigue estando en la lista
    // configurada: useCenterData conserva el ultimo centro visitado al entrar
    // al dashboard y no debe aparecer como fila fantasma.
    const isConfiguredCenter =
      Boolean(currentKey) &&
      centrosDisponibles.some(
        (item) => getCenterIdentity(item) === currentKey
      );

    if (isConfiguredCenter) {
      const existingIndex = combinedCenters.findIndex(
        (center) => getCenterKey(center, centrosDisponibles) === currentKey
      );

      if (existingIndex < 0) {
        combinedCenters.unshift(centerData);
      } else if (
        parseDateTime(centerData?.hora) >
        parseDateTime(combinedCenters[existingIndex]?.hora)
      ) {
        combinedCenters[existingIndex] = centerData;
      }
    }

    return combinedCenters;
  }, [allCentersData, centerData, centrosDisponibles]);

  const enrichedCentersData = useMemo(
    () => dashboardSourceData.map((center) => mergeCenterMetadata(center, centrosDisponibles)),
    [dashboardSourceData, centrosDisponibles]
  );

  const dashboardCenterData = useMemo(() => {
    if (!selectedCenter?.centro) return null;

    const selectedKey = getCenterIdentity(selectedCenter);
    return (
      enrichedCentersData.find(
        (center) => getCenterKey(center, centrosDisponibles) === selectedKey
      ) || null
    );
  }, [enrichedCentersData, selectedCenter, centrosDisponibles]);

  const effectiveCenterData = useMemo(() => {
    const resolvedData = !centerData
      ? dashboardCenterData
      : !dashboardCenterData
      ? centerData
      : parseDateTime(dashboardCenterData?.hora) >= parseDateTime(centerData?.hora)
      ? dashboardCenterData
      : centerData;

    if (!resolvedData) return resolvedData;

    return {
      ...resolvedData,
      estado_centro:
        resolvedData?.estado_centro ??
        resolvedData?.estado ??
        selectedCenter?.estado_centro ??
        selectedCenter?.estado ??
        null,
    };
  }, [centerData, dashboardCenterData, selectedCenter]);

  const allModules = enrichedCentersData.flatMap(
    c => c?.modulos || []
  );

  // La carga es secuencial: hay que seguir en estado de carga mientras falten
  // centros por responder, no solo mientras no haya llegado ninguno.
  const dashboardLoading =
    showDashboardState &&
    centrosDisponibles.length > 0 &&
    !centersError &&
    loadingCenters &&
    allCentersData.length < centrosDisponibles.length;

  const handleSelectCenter = useCallback((center) => {
    if (!center?.centro || !center?.area || !center?.region) return;

    const dashboardSnapshot = enrichedCentersData.find(
      (item) => getCenterIdentity(item) === getCenterIdentity(center)
    );

    if (dashboardSnapshot) {
      saveToCenterCache(center, dashboardSnapshot);
    }

    localStorage.setItem("REGION", center.region);
    localStorage.setItem("AREA", center.area);
    localStorage.setItem("CENTRO", center.centro);

    setUserInteracted(true);
    setSelectedCenter({
      region: center.region,
      area: center.area,
      centro: center.centro,
      estado_centro:
        center?.estado_centro ??
        center?.estado ??
        dashboardSnapshot?.estado_centro ??
        dashboardSnapshot?.estado ??
        null,
    });
    setShowDashboard(false);
  }, [enrichedCentersData, setShowDashboard]);

  /* ================================
     Props
  ================================ */

  const HomeProps = {
    onSelectCenter: handleSelectCenter,
    translate,
    handleClick,
    openMenuIdioma,
    handleCloseIdioma,
    handleLanguageChange,
    idioma,
    showDashboard: showDashboardState,
    setShowDashboard,
    version,
    generadores: effectiveCenterData?.generadores,
    selectedCenter
  };

  // console.log('enrichedCentersData', enrichedCentersData)
  /* ================================
     Render
  ================================ */

  return (
    <ModuleControlsProvider>
      <TooltipModeProvider>
        <TooltipProvider>
          {initialLoading ? (
            <Cargando />
          ) : showDashboardState ? (
            <Dashboard
              modulos={allModules}
              centersData={enrichedCentersData}
              loading={dashboardLoading}
              error={centersError}
              expectedCenterCount={centrosDisponibles.length}
              onSelectCenter={handleSelectCenter}
              setShowDashboard={setShowDashboard}
              version={version}
              translate={translate}
            />
          ) : (
            <HomeNanoox
              error={error}
              datos={effectiveCenterData || {}}
              loadingCenter={initialLoading}
              controlType={controlType}
              {...HomeProps}
            />
          )}
        </TooltipProvider>
      </TooltipModeProvider>
    </ModuleControlsProvider>
  );
}

// import { useState, useEffect } from "react";
// import { useCenterData, tipo_control } from "./hooks/useCenterData";
// import { useCentersDataAllSafe } from "./hooks/useCentersDataAllSafe";
// import { useServer } from "../../context/ServerContext";

// import { TooltipModeProvider } from "./context/TooltipModeContext";
// import { TooltipProvider } from "./context/TooltipContext";
// import { ModuleControlsProvider } from "./context/ModuleControlsContext";

// import HomeNanoox from "./pages/Home/HomeNanoox";
// import Dashboard from "./pages/Dashboard/Dashboard";
// import Cargando from "./pages/Home/ui/Cargando";

// import "./shared/styles/variables.css";
// import "./shared/styles/globals.css";

// /* ================================
//    Utils
// ================================ */

// function getCentersFromStorage() {
//   try {
//     const raw = localStorage.getItem("listCenter");
//     if (!raw) return [];
//     const parsed = JSON.parse(raw);
//     return Array.isArray(parsed) ? parsed : [];
//   } catch (err) {
//     console.error("❌ Error leyendo listCenter", err);
//     return [];
//   }
// }

// function markCenterUsage(centro) {
//   const raw = localStorage.getItem("center_usage") || "{}";
//   const usage = JSON.parse(raw);
//   usage[centro] = Date.now();
//   localStorage.setItem("center_usage", JSON.stringify(usage));
// }

// function sortCentersByUsage(centros) {
//   const usage = JSON.parse(localStorage.getItem("center_usage") || "{}");
//   return [...centros].sort(
//     (a, b) => (usage[b.centro] || 0) - (usage[a.centro] || 0)
//   );
// }

// /* ================================
//    Main Component
// ================================ */

// export default function MainNanoox({
//   translate,
//   handleClick,
//   openMenuIdioma,
//   handleCloseIdioma,
//   handleLanguageChange,
//   idioma,
// }) {
//   const version = "2026.2.5";

//   const { servidor } = useServer();

//   const [page, setPage] = useState(0);
//   const [selectedCenter, setSelectedCenter] = useState(null);
//   const [showDashboard, setShowDashboard] = useState(false);
//   const [centrosDisponibles, setCentrosDisponibles] = useState([]);
//   const [controlType, setControlType] = useState("");

//   /* ================================
//      Centro activo (CRÍTICO)
//   ================================ */

//   const {
//     centerData,
//     initialLoading,
//     refreshing,
//     error,
//     refetch,
//   } = useCenterData({
//     region: selectedCenter?.region,
//     area: selectedCenter?.area,
//     centro: selectedCenter?.centro,
//     servidor,
//   });

//   /* ================================
//      Carga segura de TODOS los centros
//      (solo cuando se necesita)
//   ================================ */

//   const {
//     data: allCentersData,
//     loading: loadingCenters,
//     startLoading,
//     reset,
//   } = useCentersDataAllSafe();

//   /* ================================
//      Cargar lista de centros
//   ================================ */

//   useEffect(() => {
//     setCentrosDisponibles(getCentersFromStorage());
//   }, []);

//   /* ================================
//      Tipo de control (1 sola vez)
//   ================================ */

//   useEffect(() => {
//     const fetchControlType = async () => {
//       const result = await tipo_control(servidor);
//       setControlType(result?.datos || "");
//     };
//     fetchControlType();
//   }, [servidor]);

//   useEffect(() => {
//     if (selectedCenter) {
//       console.log(
//         `%c🎯 Centro activo seleccionado: ${selectedCenter.centro}`,
//         "color: blue; font-weight: bold"
//       );
//     }
//   }, [selectedCenter]);
//   useEffect(() => {
//     if (showDashboard) {
//       console.log(
//         "%c📊 Entrando al Dashboard → cargar TODOS los centros",
//         "color: red; font-weight: bold"
//       );
//     }
//   }, [showDashboard]);


//   useEffect(() => {
//     if (selectedCenter) return;

//     const region = localStorage.getItem("REGION");
//     const area = localStorage.getItem("AREA");
//     const centro = localStorage.getItem("CENTRO");

//     if (region && area && centro) {
//       setSelectedCenter({ region, area, centro });
//     }
//   }, [selectedCenter]);

//   /* ================================
//      Refetch centro activo cada 60s
//   ================================ */

//   useEffect(() => {
//     const interval = setInterval(() => {
//       refetch();
//     }, 60000);
//     return () => clearInterval(interval);
//   }, [refetch]);

//   /* ================================
//      Marcar uso del centro (telemetría)
//   ================================ */

//   useEffect(() => {
//     if (selectedCenter?.centro) {
//       markCenterUsage(selectedCenter.centro);
//     }
//   }, [selectedCenter]);

//   /* ================================
//       Prefetch inteligente
//      (NO bloquea, NO satura)
//   ================================ */

//   useEffect(() => {
//     if (!centerData) return;
//     if (showDashboard) return;
//     if (!selectedCenter) return;

//     const ordered = sortCentersByUsage(centrosDisponibles);

//     startLoading({
//       centros: ordered.slice(0, 5), // solo los más probables
//       servidor,
//       delay: 800,
//     });
//   }, [centerData]);

//   /* ================================
//      📊 Cargar TODOS solo al Dashboard
//   ================================ */

//   useEffect(() => {
//     if (!showDashboard) return;

//     startLoading({
//       centros: centrosDisponibles,
//       servidor,
//       delay: 500,
//     });

//     return () => reset();
//   }, [showDashboard, centrosDisponibles, servidor]);

//   /* ================================
//      Datos del Dashboard
//   ================================ */

//   const allModules = allCentersData.flatMap(
//     (c) => c?.modulos || []
//   );

//   /* ================================
//      Props
//   ================================ */

//   const HomeProps = {
//     translate,
//     handleClick,
//     openMenuIdioma,
//     handleCloseIdioma,
//     handleLanguageChange,
//     idioma,
//     onSelectCenter: setSelectedCenter,
//     showDashboard,
//     setShowDashboard,
//     version,
//   };

//   /* ================================
//      Render
//   ================================ */

//   return (
//     <ModuleControlsProvider>
//       <TooltipModeProvider>
//         <TooltipProvider>
//           <>
//             {initialLoading ? (
//               <Cargando />
//             ) : showDashboard ? (
//               <Dashboard
//                 modulos={allModules}
//                 centersData={allCentersData}
//                 loading={loadingCenters}
//                 setShowDashboard={setShowDashboard}
//               />
//             ) : (
//               <HomeNanoox
//                 error={error}
//                 datos={centerData || {}}
//                 loadingCenter={initialLoading}
//                 page={page}
//                 generadores={centerData?.generadores || {}}
//                 controlType={controlType}
//                 {...HomeProps}
//               />
//             )}
//           </>
//         </TooltipProvider>
//       </TooltipModeProvider>
//     </ModuleControlsProvider>
//   );
// }
